import React, { Component } from 'react';
import styled, { keyframes } from 'styled-components';
import { FiX, FiAlertTriangle, FiArrowRight, FiUser } from 'react-icons/fi';
import { C } from '../chrome';

// ─── Animations ────────────────────────────────────────────────────────────
const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const popIn = keyframes`
  from { opacity: 0; transform: translateY(8px) scale(0.985); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;

// ─── Layout ────────────────────────────────────────────────────────────────
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(2, 5, 12, 0.72);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9100;
  padding: 2rem;
  animation: ${fadeIn} 140ms ease-out;
`;

const Box = styled.div`
  background: ${C.surface};
  border: 1px solid ${C.border};
  border-radius: 1.4rem;
  width: 52rem;
  max-width: 100%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow:
    0 24px 48px -12px rgba(0,0,0,0.6),
    0 12px 24px -8px rgba(0,0,0,0.45),
    inset 0 1px 0 rgba(255,255,255,0.04);
  animation: ${popIn} 180ms cubic-bezier(0.4, 0, 0.2, 1);
`;

const Head = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.6rem 2rem;
  border-bottom: 1px solid ${C.border};
  flex-shrink: 0;
`;

const HeadLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const HeadIcon = styled.div`
  width: 3.4rem;
  height: 3.4rem;
  border-radius: 0.8rem;
  background: ${C.yellowDim};
  border: 1px solid ${C.yellow}40;
  color: ${C.yellow};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const HeadText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
`;

const Title = styled.div`
  font-size: 1.6rem;
  font-weight: 700;
  color: ${C.textPrimary};
  letter-spacing: -0.01em;
`;

const Sub = styled.div`
  font-size: 1.15rem;
  color: ${C.textMuted};
`;

const Close = styled.button`
  background: transparent;
  border: 1px solid ${C.border};
  color: ${C.textMuted};
  cursor: pointer;
  padding: 0.6rem;
  border-radius: 0.6rem;
  display: flex;
  transition: all 0.15s;
  &:hover {
    border-color: ${C.red}60;
    background: ${C.redDim};
    color: ${C.red};
  }
`;

const Body = styled.div`
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1.8rem;
  overflow-y: auto;
`;

// ─── Subject card ──────────────────────────────────────────────────────────
const SubjectCard = styled.div`
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 1.2rem;
  background: ${C.surfaceAlt};
  border: 1px solid ${C.border};
  border-radius: 0.8rem;
  padding: 1.2rem 1.4rem;
`;

const Avatar = styled.div`
  width: 4rem;
  height: 4rem;
  border-radius: 50%;
  background: ${C.purpleDim};
  border: 1px solid ${C.purple}33;
  color: ${C.purple};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.6rem;
  font-weight: 700;
  flex-shrink: 0;
`;

const SubjectMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const SubjectName = styled.div`
  font-size: 1.4rem;
  font-weight: 600;
  color: ${C.textPrimary};
`;

const SubjectSub = styled.div`
  font-size: 1.15rem;
  color: ${C.textMuted};
`;

const Arrow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  font-size: 1.1rem;
  color: ${C.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-weight: 600;
`;

const PoolPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: ${(p) => p.color || C.textMuted};
  background: ${(p) => (p.color ? `${p.color}1a` : C.surfaceAlt)};
  border: 1px solid ${(p) => (p.color ? `${p.color}40` : C.border)};
  padding: 0.3rem 0.8rem;
  border-radius: 9999px;
`;

// ─── Reason field ──────────────────────────────────────────────────────────
const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
`;

const SectionHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
`;

const SectionLabel = styled.div`
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${C.textMuted};
`;

const Counter = styled.div`
  font-size: 1.05rem;
  font-weight: 600;
  color: ${(p) => (p.invalid ? C.red : C.textDim)};
  font-variant-numeric: tabular-nums;
`;

const TextArea = styled.textarea`
  background: ${C.surfaceAlt};
  border: 1.5px solid ${(p) => (p.invalid ? C.red : C.border)};
  border-radius: 0.8rem;
  padding: 1rem 1.2rem;
  font-size: 1.3rem;
  line-height: 1.55;
  color: ${C.textPrimary};
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  resize: vertical;
  min-height: 9rem;
  max-height: 18rem;
  transition: all 0.15s;

  &::placeholder { color: ${C.textDim}; }
  &:focus {
    outline: none;
    border-color: ${C.purple};
    box-shadow: 0 0 0 3px ${C.purpleDim};
  }
`;

const Hint = styled.div`
  font-size: 1.1rem;
  color: ${C.textDim};
`;

const ErrorStrip = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  font-size: 1.15rem;
  color: ${C.red};
  background: ${C.redDim};
  border: 1px solid ${C.red}40;
  border-radius: 0.6rem;
  padding: 0.8rem 1rem;
`;

// ─── Footer ────────────────────────────────────────────────────────────────
const Foot = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  padding: 1.4rem 2rem;
  border-top: 1px solid ${C.border};
  flex-shrink: 0;
  background: ${C.surface};
`;

const FootHint = styled.div`
  font-size: 1.05rem;
  color: ${C.textDim};
  kbd {
    background: ${C.surfaceAlt};
    border: 1px solid ${C.border};
    border-radius: 0.4rem;
    padding: 0.15rem 0.5rem;
    font-family: ui-monospace, 'SF Mono', monospace;
    font-size: 0.95rem;
    color: ${C.textMuted};
    margin: 0 0.15rem;
  }
`;

const FootActions = styled.div`
  display: flex;
  gap: 0.8rem;
`;

const Btn = styled.button`
  border-radius: 0.7rem;
  padding: 0.9rem 1.6rem;
  font-size: 1.2rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  border: 1px solid ${C.border};
  background: ${C.surfaceAlt};
  color: ${C.textSecondary};
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  &:hover { color: ${C.textPrimary}; border-color: ${C.borderLight}; }
  &:disabled { opacity: 0.45; cursor: not-allowed; }
`;

const PrimaryBtn = styled(Btn)`
  background: ${C.purple};
  color: #fff;
  border-color: ${C.purple};
  box-shadow: 0 1px 0 rgba(255,255,255,0.12) inset, 0 4px 12px ${C.purple}30;
  &:hover { background: #9a67d8; color: #fff; border-color: #9a67d8; }
  &:disabled { background: ${C.surfaceAlt}; color: ${C.textDim}; box-shadow: none; border-color: ${C.border}; }
`;

// ─── Helpers ───────────────────────────────────────────────────────────────
const REASON_MIN = 5;
const REASON_MAX = 255;

const initials = (name) => {
  if (!name) return '·';
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0].toUpperCase())
    .join('');
};

// ─── Component ─────────────────────────────────────────────────────────────
class RoleChangeModal extends Component {
  state = {
    reason:     '',
    submitting: false,
    error:      '',
  };

  textareaRef = null;

  componentDidUpdate(prev) {
    if (this.props.open && !prev.open) {
      this.setState({ reason: '', submitting: false, error: '' });
      setTimeout(() => this.textareaRef && this.textareaRef.focus(), 30);
    }
  }

  setReason = (e) => this.setState({ reason: e.target.value, error: '' });

  onKeyDown = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      this.submit();
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      if (this.props.onClose) this.props.onClose();
    }
  };

  submit = async () => {
    const { onSubmit, agent } = this.props;
    const trimmed = this.state.reason.trim();

    if (trimmed.length < REASON_MIN) {
      this.setState({ error: `Reason must be at least ${REASON_MIN} characters.` });
      return;
    }
    if (trimmed.length > REASON_MAX) {
      this.setState({ error: `Reason must be ${REASON_MAX} characters or fewer.` });
      return;
    }

    this.setState({ submitting: true, error: '' });
    const result = await onSubmit(agent.id, trimmed);

    if (result.ok) {
      // Caller closes the modal and surfaces a toast / success message.
      this.setState({ submitting: false });
    } else {
      this.setState({
        submitting: false,
        error: result.message || 'Failed to mark as tele-caller.',
      });
    }
  };

  render() {
    const { open, agent, onClose } = this.props;
    const { reason, submitting, error } = this.state;

    if (!open || !agent) return null;

    const trimmedLen = reason.trim().length;
    const overMax    = trimmedLen > REASON_MAX;
    const canSubmit  = !submitting && trimmedLen >= REASON_MIN && !overMax;

    return (
      <Overlay onClick={onClose}>
        <Box onClick={(e) => e.stopPropagation()} onKeyDown={this.onKeyDown}>
          <Head>
            <HeadLeft>
              <HeadIcon><FiAlertTriangle size={18} /></HeadIcon>
              <HeadText>
                <Title>Mark as Tele-caller</Title>
                <Sub>This moves the agent out of the doctor pool.</Sub>
              </HeadText>
            </HeadLeft>
            <Close onClick={onClose} aria-label="Close"><FiX size={18} /></Close>
          </Head>

          <Body>
            <SubjectCard>
              <Avatar>{initials(agent.name)}</Avatar>
              <SubjectMeta>
                <SubjectName>{agent.name || `#${agent.id}`}</SubjectName>
                <SubjectSub>
                  <FiUser size={11} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                  Currently a doctor · admin user
                </SubjectSub>
              </SubjectMeta>
              <Arrow>
                <PoolPill color={C.purple}>Doctor</PoolPill>
                <FiArrowRight size={14} />
                <PoolPill color={C.green}>Tele-caller</PoolPill>
              </Arrow>
            </SubjectCard>

            <Section>
              <SectionHead>
                <SectionLabel>Reason (required)</SectionLabel>
                <Counter invalid={overMax}>
                  {trimmedLen} / {REASON_MAX}
                </Counter>
              </SectionHead>
              <TextArea
                ref={(r) => { this.textareaRef = r; }}
                placeholder="e.g. promoting after telecalling training, per Anuj's email"
                value={reason}
                onChange={this.setReason}
                onKeyDown={this.onKeyDown}
                invalid={overMax}
                spellCheck="false"
              />
              <Hint>
                Min {REASON_MIN} characters. Visible in the agent's audit log.
              </Hint>
            </Section>

            {error && (
              <ErrorStrip>
                <FiAlertTriangle size={14} />
                {error}
              </ErrorStrip>
            )}
          </Body>

          <Foot>
            <FootHint>
              <kbd>esc</kbd> to cancel · <kbd>⌘</kbd><kbd>↵</kbd> to confirm
            </FootHint>
            <FootActions>
              <Btn onClick={onClose} disabled={submitting}>Cancel</Btn>
              <PrimaryBtn onClick={this.submit} disabled={!canSubmit}>
                {submitting ? 'Marking…' : 'Mark as Tele-caller'}
                {!submitting && <FiArrowRight size={14} />}
              </PrimaryBtn>
            </FootActions>
          </Foot>
        </Box>
      </Overlay>
    );
  }
}

export default RoleChangeModal;
